// Gemini API Configuration
// Görsel hazırlama modülü için yapay zeka servisi

// E-ticaret platform boyut şablonları
export const IMAGE_SIZE_PRESETS = [
    { id: 'instagram-post', name: 'Instagram Post', width: 1080, height: 1080, aspectRatio: '1:1' },
    { id: 'instagram-story', name: 'Instagram Story', width: 1080, height: 1920, aspectRatio: '9:16' },
    { id: 'trendyol', name: 'Trendyol', width: 1100, height: 1800, aspectRatio: '11:18' },
    { id: 'hepsiburada', name: 'Hepsiburada', width: 500, height: 500, aspectRatio: '1:1' },
    { id: 'amazon', name: 'Amazon', width: 1000, height: 1000, aspectRatio: '1:1' },
    { id: 'n11', name: 'N11', width: 600, height: 600, aspectRatio: '1:1' },
    { id: 'web-banner', name: 'Web Banner', width: 1200, height: 628, aspectRatio: '16:9' },
    { id: 'whatsapp-catalog', name: 'WhatsApp Katalog', width: 500, height: 500, aspectRatio: '1:1' },
];

// Kullanılabilir Gemini Modelleri
export const GEMINI_MODELS = [
    {
        id: 'gemini-2.0-flash-exp-image-generation',
        name: 'Gemini 2.0 Flash (Görsel)',
        description: 'Hızlı görsel oluşturma ve düzenleme',
        supportsImageGen: true
    },
    {
        id: 'gemini-2.0-flash',
        name: 'Gemini 2.0 Flash',
        description: 'Hızlı ve çok yönlü model',
        supportsImageGen: false
    },
    {
        id: 'gemini-1.5-pro',
        name: 'Gemini 1.5 Pro',
        description: 'Gelişmiş anlama ve analiz',
        supportsImageGen: false
    },
    {
        id: 'gemini-1.5-flash',
        name: 'Gemini 1.5 Flash',
        description: 'Hızlı ve ekonomik',
        supportsImageGen: false
    },
];

// Varsayılan model
export const DEFAULT_GEMINI_MODEL = 'gemini-2.0-flash-exp-image-generation';

// Gemini API URL
const GEMINI_API_URL = 'https://generativelanguage.googleapis.com/v1beta/models';

/**
 * Görsel düzenleme için Gemini API'ye istek gönderir
 * @param {string} imageBase64 - Base64 formatında görsel
 * @param {string} prompt - Düzenleme tarifi
 * @param {string} apiKey - Gemini API anahtarı
 * @param {string} modelId - Kullanılacak model ID (opsiyonel)
 * @param {string} aspectRatio - Çıkış boyut oranı (örn: "1:1", "16:9")
 * @returns {Promise<{imageBase64: string, text?: string}>}
 */
export async function editImageWithGemini(imageBase64, prompt, apiKey, modelId = DEFAULT_GEMINI_MODEL, aspectRatio = '1:1', referenceImageBase64 = null) {
    if (!apiKey) {
        throw new Error('Gemini API anahtarı bulunamadı. Lütfen Ayarlar > API Anahtarları bölümünden API anahtarınızı girin.');
    }

    const endpoint = `${GEMINI_API_URL}/${modelId}:generateContent?key=${apiKey}`;

    // Prompt'a boyut oranını ekle
    let enhancedPrompt = `${prompt}\n\nOutput Aspect Ratio: ${aspectRatio}. Ensure the subject fits well within this aspect ratio.`;

    if (referenceImageBase64) {
        enhancedPrompt += "\n\nUse the second image as a style or content reference as described in the prompt.";
    }

    // Görsel MIME tipini belirle
    const getMimeType = (b64) => {
        if (b64.includes('image/jpeg') || b64.includes('image/jpg')) return 'image/jpeg';
        if (b64.includes('image/webp')) return 'image/webp';
        return 'image/png';
    };

    const mimeType = getMimeType(imageBase64);
    // Base64 data URL'den sadece data kısmını al
    const base64Data = imageBase64.includes(',') ? imageBase64.split(',')[1] : imageBase64;

    const parts = [
        { text: enhancedPrompt },
        {
            inlineData: {
                mimeType: mimeType,
                data: base64Data
            }
        }
    ];

    // Referans görsel varsa ekle
    if (referenceImageBase64) {
        const refMimeType = getMimeType(referenceImageBase64);
        const refBase64Data = referenceImageBase64.includes(',') ? referenceImageBase64.split(',')[1] : referenceImageBase64;
        parts.push({
            inlineData: {
                mimeType: refMimeType,
                data: refBase64Data
            }
        });
    }

    const requestBody = {
        contents: [{
            parts: parts
        }],
        generationConfig: {
            responseModalities: ['Text', 'Image']
        }
    };

    try {
        const response = await fetch(endpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error?.message || `API hatası: ${response.status}`);
        }

        const data = await response.json();

        // Yanıttan görsel ve metni çıkar
        const result = { imageBase64: null, text: null };

        if (data.candidates && data.candidates[0]?.content?.parts) {
            for (const part of data.candidates[0].content.parts) {
                if (part.text) {
                    result.text = part.text;
                }
                if (part.inlineData?.data) {
                    result.imageBase64 = `data:${part.inlineData.mimeType};base64,${part.inlineData.data}`;
                }
            }
        }

        if (!result.imageBase64) {
            throw new Error('API yanıtında görsel bulunamadı');
        }

        return result;
    } catch (error) {
        console.error('Gemini API hatası:', error);
        throw error;
    }
}

/**
 * Görseli tarayıcıda yeniden boyutlandırır
 * @param {string} imageBase64 - Base64 formatında görsel
 * @param {number} targetWidth - Hedef genişlik
 * @param {number} targetHeight - Hedef yükseklik
 * @returns {Promise<string>} - Yeniden boyutlandırılmış base64 görsel
 */
export function resizeImage(imageBase64, targetWidth, targetHeight) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = targetWidth;
            canvas.height = targetHeight;
            const ctx = canvas.getContext('2d');

            // Beyaz arka plan
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, targetWidth, targetHeight);

            // Aspect ratio koruyarak boyutlandır
            const scale = Math.min(targetWidth / img.width, targetHeight / img.height);
            const scaledWidth = img.width * scale;
            const scaledHeight = img.height * scale;
            const x = (targetWidth - scaledWidth) / 2;
            const y = (targetHeight - scaledHeight) / 2;

            ctx.drawImage(img, x, y, scaledWidth, scaledHeight);

            resolve(canvas.toDataURL('image/png'));
        };
        img.onerror = reject;
        img.src = imageBase64;
    });
}

/**
 * Base64 görseli Blob'a dönüştürür
 * @param {string} base64 - Base64 görsel
 * @returns {Blob}
 */
export function base64ToBlob(base64) {
    const parts = base64.split(';base64,');
    const contentType = parts[0].split(':')[1];
    const raw = window.atob(parts[1]);
    const rawLength = raw.length;
    const uInt8Array = new Uint8Array(rawLength);

    for (let i = 0; i < rawLength; ++i) {
        uInt8Array[i] = raw.charCodeAt(i);
    }

    return new Blob([uInt8Array], { type: contentType });
}

/**
 * Görseli indirir
 * @param {string} base64 - Base64 görsel
 * @param {string} filename - Dosya adı
 */
export function downloadImage(base64, filename) {
    const link = document.createElement('a');
    link.href = base64;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}
